import { defineMessages } from "../i18n/core";

export const storageMessages = defineMessages({
  "pt-BR": {
    title: "Armazenamento",
    lede: "O espaço é fornecido pela plataforma. Você contrata volume; destino, cifragem e redundância ficam por nossa conta.",
    summaryTitle: "Volume contratado",
    summarySub: "Somando todas as áreas.",
    contracted: "Contratado",
    used: "Ocupado",
    usedHint: "Espaço ocupado depois da deduplicação; é a unidade da franquia. Inclui dados de pontos já apagados até a próxima manutenção do repositório.",
    free: "Livre",
    unlimited: "Sem limite",
    pctUsed: (pct: string) => `${pct} do contratado`,
    areas: "Áreas de armazenamento",
    areasSub: "Cada área tem o próprio volume contratado.",
    noAreas: "Nenhuma área contratada ainda. Fale com quem cuida da sua conta.",
    replicated: "Com redundância",
    usedOf: (used: string, quota: string) => `${used} de ${quota}`,
    usedNoLimit: (used: string) => `${used} guardados, sem limite de volume`,
    freeOf: (size: string) => `${size} livres`,
    howTitle: "Como funciona",
    how1: "O agente cifra os dados na sua máquina antes de enviar. A plataforma guarda blocos que não consegue ler.",
    how2: "Escolher o destino, verificar a saúde e trocar hardware é trabalho da plataforma: não há nada para configurar aqui.",
    how3: "Uma área com redundância mantém uma segunda cópia em outro local, verificada automaticamente.",
    how4: "Precisa de mais espaço? Fale com quem cuida da sua conta para aumentar o contratado.",
    nearTitle: "Armazenamento quase cheio",
    nearBody: (pct: string, free: string) => `Você já usa ${pct} do contratado e restam ${free}. Ao chegar a 100%, os backups novos passam a ser recusados.`,
    fullTitle: "Armazenamento esgotado",
    fullBody: (pct: string) =>
      `Você usa ${pct} do contratado, então os backups novos estão sendo recusados. O que já foi guardado continua íntegro e a restauração funciona normalmente, mesmo acima do contratado.`,
    howToFree: "Para liberar espaço, reduza a retenção nas políticas (os pontos de restauração mais antigos saem na próxima manutenção) ou peça mais volume a quem cuida da sua conta.",
    openStorage: "Ver armazenamento",
    openPolicies: "Ver políticas",
  },
  "en-US": {
    title: "Storage",
    lede: "Space is provided by the platform. You contract volume; destination, encryption and redundancy are on us.",
    summaryTitle: "Contracted volume",
    summarySub: "Across every area.",
    contracted: "Contracted",
    used: "Used",
    usedHint: "Space used after deduplication; it is the unit of the quota. It includes data from deleted restore points until the next repository maintenance.",
    free: "Free",
    unlimited: "No limit",
    pctUsed: (pct: string) => `${pct} of the contracted volume`,
    areas: "Storage areas",
    areasSub: "Each area has its own contracted volume.",
    noAreas: "No area contracted yet. Talk to whoever manages your account.",
    replicated: "Redundant",
    usedOf: (used: string, quota: string) => `${used} of ${quota}`,
    usedNoLimit: (used: string) => `${used} stored, no volume limit`,
    freeOf: (size: string) => `${size} free`,
    howTitle: "How it works",
    how1: "The agent encrypts data on your machine before sending it. The platform stores blocks it cannot read.",
    how2: "Choosing the destination, checking its health and replacing hardware is the platform's job: there is nothing to configure here.",
    how3: "An area with redundancy keeps a second copy in another location, verified automatically.",
    how4: "Need more space? Talk to whoever manages your account to raise the contracted volume.",
    nearTitle: "Storage almost full",
    nearBody: (pct: string, free: string) => `You are using ${pct} of the contracted volume and ${free} remain. At 100%, new backups start being refused.`,
    fullTitle: "Storage full",
    fullBody: (pct: string) =>
      `You are using ${pct} of the contracted volume, so new backups are being refused. What is already stored stays intact and restores keep working, even above the contracted volume.`,
    howToFree: "To free up space, shorten retention in the policies (the oldest restore points go out on the next maintenance run) or ask for more volume from whoever manages your account.",
    openStorage: "View storage",
    openPolicies: "View policies",
  },
  es: {
    title: "Almacenamiento",
    lede: "El espacio lo provee la plataforma. Tú contratas volumen; el destino, el cifrado y la redundancia corren por nuestra cuenta.",
    summaryTitle: "Volumen contratado",
    summarySub: "Sumando todas las áreas.",
    contracted: "Contratado",
    used: "Ocupado",
    usedHint: "Espacio ocupado tras la deduplicación; es la unidad de la cuota. Incluye datos de puntos ya borrados hasta el siguiente mantenimiento del repositorio.",
    free: "Libre",
    unlimited: "Sin límite",
    pctUsed: (pct: string) => `${pct} de lo contratado`,
    areas: "Áreas de almacenamiento",
    areasSub: "Cada área tiene su propio volumen contratado.",
    noAreas: "Todavía no hay ningún área contratada. Habla con quien gestiona tu cuenta.",
    replicated: "Con redundancia",
    usedOf: (used: string, quota: string) => `${used} de ${quota}`,
    usedNoLimit: (used: string) => `${used} guardados, sin límite de volumen`,
    freeOf: (size: string) => `${size} libres`,
    howTitle: "Cómo funciona",
    how1: "El agente cifra los datos en tu equipo antes de enviarlos. La plataforma guarda bloques que no puede leer.",
    how2: "Elegir el destino, vigilar su salud y cambiar el hardware es tarea de la plataforma: aquí no hay nada que configurar.",
    how3: "Un área con redundancia mantiene una segunda copia en otro lugar, verificada automáticamente.",
    how4: "¿Necesitas más espacio? Habla con quien gestiona tu cuenta para ampliar lo contratado.",
    nearTitle: "Almacenamiento casi lleno",
    nearBody: (pct: string, free: string) => `Ya usas ${pct} de lo contratado y quedan ${free}. Al llegar al 100%, las copias nuevas empiezan a rechazarse.`,
    fullTitle: "Almacenamiento agotado",
    fullBody: (pct: string) =>
      `Usas ${pct} de lo contratado, así que las copias nuevas se están rechazando. Lo que ya está guardado sigue íntegro y la restauración funciona con normalidad, incluso por encima de lo contratado.`,
    howToFree: "Para liberar espacio, reduce la retención en las políticas (los puntos de restauración más antiguos salen en el próximo mantenimiento) o pide más volumen a quien gestiona tu cuenta.",
    openStorage: "Ver almacenamiento",
    openPolicies: "Ver políticas",
  },
});
